import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  FolderOpen,
  Play,
  Video,
} from "lucide-react";
import { POKUTILS } from "../../config/API";

const PageShell = ({ children }) => (
  <div className="max-w-6xl mx-auto">
    <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
      {children}
    </div>
  </div>
);

const PageHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="relative border-b border-gray-100 bg-gradient-to-r from-primary/5 via-white to-white px-5 py-4 sm:px-6">
    <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-primary/80 to-primary/40" />
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <div>
        <h1 className="text-lg font-bold tracking-tight text-gray-900 sm:text-xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-0.5 max-w-2xl text-xs text-gray-500 sm:text-sm">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  </div>
);

const BackButton = ({ onClick, label = "Back to Departments" }) => (
  <button
    onClick={onClick}
    className="group mb-6 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
  >
    <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
    {label}
  </button>
);

const LoadingSkeleton = ({ variant = "departments" }) => (
  <PageShell>
    <div className="border-b border-gray-100 px-5 py-4 sm:px-6">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 animate-pulse rounded-lg bg-gray-200" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-64 animate-pulse rounded bg-gray-100" />
        </div>
      </div>
    </div>
    <div className="p-6 sm:p-8">
      {variant === "departments" ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-xl border border-gray-100 bg-gray-50"
            />
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="aspect-video w-full animate-pulse rounded-xl bg-gray-200" />
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-100" />
            ))}
          </div>
        </div>
      )}
    </div>
  </PageShell>
);

const ErrorState = ({ message, onRetry }) => (
  <PageShell>
    <div className="flex flex-col items-center px-6 py-16 text-center sm:px-8">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
        <AlertCircle className="h-7 w-7" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900">Unable to load content</h2>
      <p className="mt-2 max-w-md text-sm text-gray-600">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-6 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary/90"
        >
          Try Again
        </button>
      )}
    </div>
  </PageShell>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-6 py-14 text-center">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-gray-400">
      <Icon className="h-7 w-7" />
    </div>
    <h3 className="text-base font-semibold text-gray-800">{title}</h3>
    <p className="mt-1 max-w-sm text-sm text-gray-500">{description}</p>
  </div>
);

const Videos = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const department = searchParams.get("department");

  const [departments, setDepartments] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const fetchDepartments = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${POKUTILS}?action=getvideodepartments`, {
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status === "success" && Array.isArray(data.data)) {
          setDepartments(data.data);
        } else {
          throw new Error(data.message || "Failed to load departments");
        }
      } catch (err) {
        console.error("Error fetching video departments:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchDepartmentVideos = async () => {
      setLoading(true);
      setError(null);
      setActiveVideo(null);

      try {
        const response = await fetch(
          `${POKUTILS}?action=getdepartmentvideos&department=${encodeURIComponent(department)}`,
          { headers: { Accept: "application/json" } }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status === "success" && Array.isArray(data.data)) {
          setVideos(data.data);
          if (data.data.length > 0) {
            setActiveVideo(data.data[0]);
          }
        } else {
          throw new Error(data.message || "Failed to load videos");
        }
      } catch (err) {
        console.error("Error fetching department videos:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (department) {
      fetchDepartmentVideos();
    } else {
      fetchDepartments();
    }
  }, [department]);

  const handleDepartmentClick = (deptName) => {
    navigate(`/page?itype=videos&department=${encodeURIComponent(deptName)}`);
  };

  const handleBack = () => {
    navigate("/page?itype=videos");
  };

  const handleVideoClick = (video) => {
    setActiveVideo(video);
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    window.location.reload();
  };

  if (loading) {
    return <LoadingSkeleton variant={department ? "videos" : "departments"} />;
  }

  if (error) {
    return (
      <>
        <ErrorState message={error} onRetry={handleRetry} />
        {department && (
          <div className="mt-4 text-center">
            <BackButton onClick={handleBack} />
          </div>
        )}
      </>
    );
  }

  if (department) {
    return (
      <PageShell>
        <PageHeader
          icon={Clapperboard}
          title={`${department} Videos`}
          subtitle="Select a video from the playlist to watch training and informational content."
        />

        <div className="p-6 sm:p-8">
          <BackButton onClick={handleBack} />

          {videos.length === 0 ? (
            <EmptyState
              icon={Video}
              title="No videos available"
              description="There are no videos published for this department yet. Please check back later."
            />
          ) : (
            <div className="grid gap-8 lg:grid-cols-5">
              <div className="lg:col-span-3">
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-black shadow-lg">
                  {activeVideo ? (
                    <>
                      <div className="aspect-video w-full bg-black">
                        <video
                          key={activeVideo.videolink}
                          src={activeVideo.videolink}
                          controls
                          className="h-full w-full"
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      <div className="border-t border-gray-800 bg-gray-900 px-5 py-4">
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-400">
                          Now Playing
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-white">
                          {activeVideo.videotitle}
                        </h2>
                      </div>
                    </>
                  ) : (
                    <div className="flex aspect-video items-center justify-center bg-gray-900 text-gray-500">
                      <Play className="h-12 w-12 opacity-40" />
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="rounded-xl border border-gray-200 bg-gray-50/50">
                  <div className="border-b border-gray-200 px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Playlist
                    </h3>
                    <p className="text-xs text-gray-500">
                      {videos.length} video{videos.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <ul className="max-h-[420px] divide-y divide-gray-200 overflow-y-auto">
                    {videos.map((video, index) => {
                      const isActive =
                        activeVideo?.videolink === video.videolink;

                      return (
                        <li key={`${video.videolink}-${index}`}>
                          <button
                            onClick={() => handleVideoClick(video)}
                            className={`group flex w-full items-start gap-3 px-4 py-3.5 text-left transition-all ${
                              isActive
                                ? "bg-primary/10"
                                : "hover:bg-white"
                            }`}
                          >
                            <div
                              className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${
                                isActive
                                  ? "bg-primary text-white shadow-sm"
                                  : "bg-white text-gray-500 shadow-sm group-hover:bg-primary/10 group-hover:text-primary"
                              }`}
                            >
                              {isActive ? (
                                <Play className="h-4 w-4 fill-current" />
                              ) : (
                                <span className="text-xs font-bold">{index + 1}</span>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p
                                className={`text-sm font-medium leading-snug ${
                                  isActive ? "text-primary" : "text-gray-800"
                                }`}
                              >
                                {video.videotitle}
                              </p>
                              {isActive && (
                                <p className="mt-1 text-xs font-medium text-primary/70">
                                  Currently playing
                                </p>
                              )}
                            </div>
                            {!isActive && (
                              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-primary" />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        icon={Video}
        title="Video Library"
        subtitle="Browse departments to access training videos, tutorials, and informational content."
      />

      <div className="p-6 sm:p-8">
        {departments.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title="No departments available"
            description="Video categories have not been set up yet. Please contact your administrator."
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {departments.map((item, index) => (
              <button
                key={`${item.department}-${index}`}
                onClick={() => handleDepartmentClick(item.department)}
                className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <div className="absolute inset-x-0 top-0 h-0.5 scale-x-0 bg-gradient-to-r from-primary to-primary/60 transition-transform duration-300 group-hover:scale-x-100" />

                <div className="flex items-start justify-between gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                    <FolderOpen className="h-5 w-5" />
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-primary" />
                </div>

                <h3 className="mt-4 text-base font-semibold text-gray-900 transition-colors group-hover:text-primary">
                  {item.department}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  View department videos
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default Videos;
